import { faCircleNotch, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ErrorMessage from "components/reusable/ErrorMessage";
import Toolbar from "components/Toolbar";
import useToastContext from "hooks/useToastContext";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import OtpInput from "react18-input-otp";
import { apiPost } from "utils/apiFetch";
import pathObj from "utils/apiPath";
import { validationRules } from "utils/constants";

function OtpScreen() {
  const { t } = useTranslation();
  const location = useLocation();
  const notification = useToastContext();
  const navigate = useNavigate();
  const [icon1, setIcon1] = useState(true);
  const [icon2, setIcon2] = useState(true);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true, defaultValues: {} });

  const [seconds, setSeconds] = useState(59);
  const [minutes, setMinutes] = useState(1);
  let timerRef;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    timerRef = setInterval(() => {
      if (minutes >= 0) {
        if (seconds === 0) {
          if (minutes > 0) {
            setSeconds(60);
            setMinutes((prev) => prev - 1);
          } else {
            setSeconds(0);
          }
        } else {
          setSeconds((prev) => prev - 1);
        }
      } else {
        clearInterval(timerRef);
      }
    }, 1000);
    return () => clearInterval(timerRef);
  });

  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(null);
  const onSubmit = async (data) => {
    if (minutes === 0 && seconds === 0) {
      notification.error(t("OTP_EXPIRE"));
      return;
    }
    if (!otp) {
      return;
    }
    let res;
    try {
      setLoading(true);
      let sendData = {};

      if (location.state.type === "email") {
        sendData.type = "email";
        sendData.email = location.state.email;
      } else if (location.state.type === "mobile") {
        sendData.type = "mobile";
        sendData.mobile = location.state.mobile;
        sendData.country_code = "355";
      } else if (location.state.type === "username") {
        sendData.type = "username";
        sendData.username = location.state.username;
      }
      sendData.otp = otp;
      sendData.password = data.password;

      res = await apiPost(pathObj.forgotPasswordOtp, sendData);
      if (res.data.success) {
        notification.success(res.data.message);
        reset();

        navigate("/login");
      } else {
        notification.error(res?.data?.message);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value) => setOtp(value);

  const handleResendOtp = async () => {
    try {
      setLoading(true);
      if (minutes === 0 && seconds === 0) {
        let sendData = {};
        if (location?.state?.type === "email") {
          sendData.type = "email";
          sendData.email = location?.state?.email;
        } else if (location?.state?.type === "mobile") {
          sendData.type = "mobile";
          sendData.mobile = location?.state?.mobile;
          sendData.country_code = "355";
        } else if (location?.state?.type === "username") {
          sendData.type = "username";
          sendData.username = location?.state?.mobile;
        }
        const res = await apiPost(pathObj.forgotPassword, sendData);
        if (res.data.success === true) {
          notification.success(res.data.message);
          setSeconds(59);
          setMinutes(1);
        } else {
          notification.error(res.data.message);
        }
      } else {
        notification.error(t("WAIT_FOR_OTP"));
      }
    } catch (err) {}
    setLoading(false);
  };

  const password = watch("password");
  useEffect(() => {
    if (!isEmpty(password)) {
      trigger("confirm_password");
    }
  }, [password, trigger]);

  const preventSpacePass = (e) => {
    e.target.value = e.target.value.trimStart();
    e.target.value = e.target.value.replace(/  +/g, " ");
  };

  return (
    <>
      <Toolbar showBackBtn={true} title={t("OTP")}></Toolbar>
      <form onSubmit={handleSubmit(onSubmit)} className="flex w-full items-center justify-center">
        <div className="flex flex-col justify-center items-center md:w-[25rem]">
          <div className="mb-4">
            <OtpInput
              inputStyle={{
                border: "1.5px solid var(--card-color)",
                width: "5rem",
                background: "transparent",
                height: "2.4rem",
                color: "var(--text-color)",
                fontWeight: "bold",
                marginRight: ".2rem",
                marginLeft: ".2rem",
                outline: "none",
                borderRadius: "8px",
              }}
              shouldAutoFocus
              isInputNum={true}
              value={otp}
              containerStyle="text-center mt-2"
              onChange={(value) => handleChange(value)}
              numInputs={4}
              separator={<span> </span>}
            />

            <ErrorMessage message={errors?.otp_phone?.message} />
          </div>

          <div className="mb-4 w-full">
            {(seconds > 0 || minutes > 0) && (
              <div className="d-flex justify-content-between mt-4">
                <div className="font-medium">
                  {t("OTP_EXPIRING")} {minutes}:{seconds}
                </div>
              </div>
            )}
            <ErrorMessage message={errors?.otp_email?.message} />
          </div>

          <div className="w-full">
            <div className="mb-5 relative">
              <input
                type={icon1 ? "password" : "text"}
                id="password"
                className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                placeholder={t("O_PASSWORD")}
                onInput={preventSpacePass}
                autoComplete="new-password"
                {...register("password", {
                  required: t("REGISTER_PASSWORD"),
                  pattern: {
                    value: validationRules.password,
                    message: validationRules.passwordMessage,
                  },
                })}
                required
              />
              {icon1 ? (
                <span className="password_view cursor-pointer absolute top-[0.8rem] right-[1rem]" onClick={() => setIcon1(!icon1)}>
                  <FontAwesomeIcon icon={faEye} />
                </span>
              ) : (
                <span className="password_view cursor-pointer absolute top-[0.8rem] right-[1rem]" onClick={() => setIcon1(!icon1)}>
                  <FontAwesomeIcon icon={faEyeSlash} />
                </span>
              )}
              <ErrorMessage message={errors?.password?.message} />
            </div>

            <div className="mb-5 relative">
              <input
                type={icon2 ? "password" : "text"}
                id="confirm_password"
                className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                placeholder={t("O_NEW_PASSWORD")}
                onInput={preventSpacePass}
                autoComplete="new-password"
                {...register("confirm_password", {
                  required: t("REGISTER_PASSWORD"),
                  pattern: {
                    value: validationRules.password,
                    message: validationRules.passwordMessage,
                  },
                })}
                required
              />
              {icon2 ? (
                <span className="password_view cursor-pointer absolute top-[0.8rem] right-[1rem]" onClick={() => setIcon2(!icon2)}>
                  <FontAwesomeIcon icon={faEye} />
                </span>
              ) : (
                <span className="password_view cursor-pointer absolute top-[0.8rem] right-[1rem]" onClick={() => setIcon2(!icon2)}>
                  <FontAwesomeIcon icon={faEyeSlash} />
                </span>
              )}
              <ErrorMessage message={errors?.confirm_password?.message} />
            </div>
          </div>

          <div className="w-full">
            {loading ? (
              <div className="btn btn_theme w-100 py-3 mb-3 text-center">
                <FontAwesomeIcon icon={faCircleNotch} className="text-white text-xl animate-spin" />
              </div>
            ) : (
              <button className="btn btn_theme w-full text-white text-center" type="submit">
                {t("O_VERIFY")}
              </button>
            )}
          </div>
          <div className="flex gap-1 justify-center text-center w-100 mb-2 mt-4">
            <span className="fw-bold">{t("DIDNT_RECEIVE_OTP?")}</span>
            <div onClick={handleResendOtp} className="link text-primary font-medium" style={{ cursor: "pointer" }}>
              {" "}
              {t("RESEND_OTP")}
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default OtpScreen;
